// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noExplicitAny: TODO - address later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import type { InfiniteData } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import {
  ExternalLink,
  Filter,
  Info,
  Loader2,
  Search,
  UnfoldHorizontal,
  X,
} from "lucide-react";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import { AccountsColumnSelector } from "@/components/accounts-column-selector";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { CSVExportButton } from "@/components/csv-export-button";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import type { FilterValue } from "@/components/filter-popover";
import ResetTableMenu from "@/components/reset-table-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
// Popover no longer used after converting to InfoTooltip
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSavedViewContextOptional } from "@/contexts/saved-view-context";
import { useAccountOpportunityStats } from "@/hooks/api/use-account-opportunity-stats";
import {
  type Account,
  type AccountsResponse,
  useAccountsExport,
  useInfiniteAccounts,
} from "@/hooks/api/use-accounts";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { useFieldDefinitions } from "@/hooks/api/use-field-definitions";
import { useAccountFilterState } from "@/hooks/use-account-filter-state";
import { useAccountListFilters } from "@/hooks/use-account-list-filters";
import { useAvailableFilterFields } from "@/hooks/use-available-filter-fields";
import {
  type SelectableColumn,
  useAccountColumnSelection,
} from "@/hooks/use-column-selection";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import { useAccountCustomFieldSelection } from "@/hooks/use-custom-field-selection";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSavedViewState } from "@/hooks/use-saved-view-state";
import { useSearchState } from "@/hooks/use-search-state";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDate, formatDateForFilename } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import {
  type FilterClearingInfo,
  type FilterColumn,
  processMetricCardFilters,
} from "@/lib/filter-display-utils";
import { cn } from "@/lib/utils";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { getAccountColumnDefs, getAccountColumns } from "./account-columns";

// Static set of touchpoint metric column keys that trigger performance warnings
const TOUCHPOINT_METRIC_COLUMNS = new Set([
  "metric_touchpoint_count",
  "metric_first_touchpoint_date",
  "metric_last_touchpoint_date",
]);

interface AccountDetailsSheetProps {
  account: Account | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function AccountDetailsSheet({
  account,
  isOpen,
  onOpenChange,
}: AccountDetailsSheetProps) {
  const { data: connections } = useExternalConnectionLinks();
  const { data: opportunityStats, isLoading: isLoadingOpportunityStats } =
    useAccountOpportunityStats(account?.id);

  if (!isOpen) return null;

  const renderAccountDetails = () => {
    if (!account) {
      return (
        <div className="flex items-center justify-center h-full">
          <Skeleton className="h-32 w-32" />
        </div>
      );
    }

    const crmUrl = getExternalConnectionLink(account, connections);

    return (
      <div className="space-y-4">
        <div className="mb-4 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/accounts/${account.id}/explorer`, "_blank");
            }}
          >
            Open in Story Explorer
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                `/accounts/${account.id}/milestone-analyses`,
                "_blank",
              );
            }}
          >
            Open Milestone Analysis
          </Button>
        </div>

        {/* Basic Details Section */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <div className="col-span-2 flex items-center gap-1 text-sm text-muted-foreground">
            <span className="font-medium min-w-[4.5rem]">Type:</span>
            <span>{account.type || "Customer"}</span>
          </div>

          <div className="col-span-2 flex items-center gap-1 text-sm text-muted-foreground">
            <span className="font-medium min-w-[4.5rem]">Created:</span>
            <span>{formatDate(account.created_date)}</span>
          </div>

          <div className="col-span-2 flex items-center gap-1 text-sm text-muted-foreground -my-1">
            <span className="font-medium min-w-[4.5rem]">Source ID:</span>
            {crmUrl && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={crmUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:bg-muted hover:text-foreground py-1 px-2 rounded-md transition-colors -ml-2 flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>{account.source_record_id}</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    View in {getExternalSystemName(account.source_record_type)}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {account.website && (
            <div className="col-span-2 flex items-center gap-1 text-sm text-muted-foreground -mt-1">
              <span className="font-medium min-w-[4.5rem]">Website:</span>
              <a
                href={account.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-muted hover:text-foreground py-1 px-2 -ml-2 rounded-md transition-colors"
              >
                {account.website}
              </a>
            </div>
          )}
        </div>

        {/* Account Summary Section */}
        <div>
          <h3 className="text-xl font-bold mb-4">Account Summary</h3>

          {/* Contacts Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              Contacts
            </h4>
            <div className="bg-muted/30 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="text-3xl font-bold">
                  {account.contact_count}
                </div>
                <div className="text-sm text-muted-foreground ml-2">
                  Total Contacts
                </div>
              </div>
            </div>
          </div>

          {/* Opportunities Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">
              Opportunities
              <InfoTooltip
                trigger={
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 ml-1 -mt-0.5"
                  >
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                }
                side="right"
                className="w-80 p-4"
              >
                <div className="space-y-3">
                  <h4 className="font-medium">Opportunity Types</h4>
                  <div className="space-y-1">
                    <h5 className="text-sm font-medium">
                      Account-Related Opportunities
                    </h5>
                    <p className="text-xs text-muted-foreground">
                      Opportunities where this account is listed as the primary
                      account in the CRM system.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <h5 className="text-sm font-medium">
                      Contact-Related Opportunities
                    </h5>
                    <p className="text-xs text-muted-foreground">
                      Opportunities linked to contacts that belong to this
                      account, but where a different account is listed as the
                      primary account.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <h5 className="text-sm font-medium">Total Opportunities</h5>
                    <p className="text-xs text-muted-foreground">
                      All opportunities related to this account, either directly
                      or through its contacts. Note: This may not equal the
                      exact sum of account and contact opportunities shown here,
                      as some opportunities may be counted in both categories.
                    </p>
                  </div>
                </div>
              </InfoTooltip>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Total Opportunities */}
              <div className="bg-muted/30 p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Total</div>
                <div className="text-3xl font-bold">
                  {isLoadingOpportunityStats ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    opportunityStats?.total_opportunity_count || 0
                  )}
                </div>
                <div className="flex justify-between mt-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Account: </span>
                    <span className="font-medium">
                      {isLoadingOpportunityStats ? (
                        <Skeleton className="h-4 w-8 inline-block" />
                      ) : (
                        opportunityStats?.account_related_opportunity_count || 0
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Contact: </span>
                    <span className="font-medium">
                      {isLoadingOpportunityStats ? (
                        <Skeleton className="h-4 w-8 inline-block" />
                      ) : (
                        opportunityStats?.contact_related_opportunity_count || 0
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Won Opportunities */}
              <div className="bg-muted/30 p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Won</div>
                <div className="text-3xl font-bold">
                  {isLoadingOpportunityStats ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    opportunityStats?.total_opportunity_won_count || 0
                  )}
                </div>
                <div className="flex justify-between mt-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Account: </span>
                    <span className="font-medium">
                      {isLoadingOpportunityStats ? (
                        <Skeleton className="h-4 w-8 inline-block" />
                      ) : (
                        opportunityStats?.account_related_opportunity_won_count ||
                        0
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Contact: </span>
                    <span className="font-medium">
                      {isLoadingOpportunityStats ? (
                        <Skeleton className="h-4 w-8 inline-block" />
                      ) : (
                        opportunityStats?.contact_related_opportunity_won_count ||
                        0
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Open Opportunities */}
              <div className="bg-muted/30 p-4 rounded-lg md:col-span-2 md:w-[calc(50%-0.5rem)]">
                <div className="text-sm text-muted-foreground mb-1">Open</div>
                <div className="text-3xl font-bold">
                  {isLoadingOpportunityStats ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    opportunityStats?.total_opportunity_open_count || 0
                  )}
                </div>
                <div className="flex justify-between mt-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Account: </span>
                    <span className="font-medium">
                      {isLoadingOpportunityStats ? (
                        <Skeleton className="h-4 w-8 inline-block" />
                      ) : (
                        opportunityStats?.account_related_opportunity_open_count ||
                        0
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Contact: </span>
                    <span className="font-medium">
                      {isLoadingOpportunityStats ? (
                        <Skeleton className="h-4 w-8 inline-block" />
                      ) : (
                        opportunityStats?.contact_related_opportunity_open_count ||
                        0
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl bg-card">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-2xl">
              {account ? account.name : "Account Overview"}
            </SheetTitle>
          </div>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] mt-6">
          {renderAccountDetails()}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

interface AccountsDataTableProps {
  /**
   * Optional data source to use instead of the default useInfiniteAccounts.
   * Used for Deep Research Reports integration.
   */
  dataSource?: {
    data: InfiniteData<AccountsResponse> | undefined;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  /**
   * Optional callback when a row is clicked.
   * If provided, this will be called instead of opening the default details sheet.
   */
  onRowClick?: (account: Account) => void;
  /**
   * Optional complete state override for DRR integration.
   * When provided, the table will use this state instead of its internal state management.
   */
  stateOverride?: {
    columns: {
      // State
      columnVisibility: Record<string, boolean>;
      selectedCustomColumns: SelectableColumn[];
      customFieldIdsParam: string;
      availableColumns: SelectableColumn[];
      selectedColumns: SelectableColumn[];
      selectedCoreColumns: SelectableColumn[];
      // Functions
      setColumnVisibility: (visibility: Record<string, boolean>) => void;
      toggleColumn: (columnId: string) => void;
      isColumnSelected: (columnId: string) => boolean;
      clearSelection: () => void;
      resetToDefault: () => void;
      getVisibleColumnDefs: <T>(columnDefs: ColumnDef<T>[]) => ColumnDef<T>[];
      // Loading/error
      isLoading?: boolean;
      error?: Error | null;
    };
    filters: {
      activeFilters: Record<string, unknown>;
      updateFilter: (key: string, value: unknown) => void;
      clearFilter: (key: string) => void;
      clearAllFilters: () => void;
    };
    getQueryParams: (options: {
      searchTerm?: string;
      columns?: unknown[];
      limit?: number;
    }) => Record<string, unknown>;
  };
  /**
   * Optional callback when filters change.
   * Used for Deep Research Reports integration to sync filter state.
   */
  onFiltersChange?: (filters: Record<string, unknown>) => void;
  /**
   * Optional right-side toolbar actions rendered next to the column selector.
   * Used by DRR to place contextual actions adjacent to the selector.
   */
  rightToolbarActions?: React.ReactNode;
  /**
   * Optional override for the CSV export function.
   * Used for Deep Research Reports to export from a different data source.
   */
  onExportCSV?: () => Promise<Record<string, unknown>[]>;
}

export function AccountsDataTable({
  dataSource,
  onRowClick: customOnRowClick,
  stateOverride,
  onFiltersChange,
  rightToolbarActions,
  onExportCSV,
}: AccountsDataTableProps = {}) {
  const ACCOUNT_METRIC_LIMIT = 20000;

  // Use the sidebar hook to get state
  const { state, isMobile } = useSidebar();
  const savedViewContext = useSavedViewContextOptional();
  const isLoadingView = savedViewContext?.isLoadingView ?? false;
  const loadedViewId = savedViewContext?.loadedViewId;
  const { resetSpecificKeys } = useSavedViewState({
    pageType: "accounts::list",
  });

  // State for sorting and searching - Replace useState search with sessionStorage
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "opportunity_count",
    direction: "desc",
  });

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("accounts::list::search");

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);

  // Relation filters state
  const {
    opportunityFilters,
    touchpointFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    clearAllFilters: clearRelationFilters,
  } = useAccountFilterState("accounts::list");
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Get field definitions for relation filter display
  const { fields: touchpointFieldDefs = [] } =
    useAvailableFilterFields("touchpoint");
  const { fields: opportunityFieldDefs = [] } =
    useAvailableFilterFields("opportunity");
  // Process relation filters for chips
  const relationFiltersProcessed = useMemo(() => {
    return processMetricCardFilters(
      { operator: "AND", filters: [] }, // no account filter group on this page
      opportunityFilters,
      touchpointFilters,
      false,
      hasOpportunityFilters,
      hasTouchpointFilters,
      [],
      opportunityFieldDefs,
      touchpointFieldDefs,
    );
  }, [
    opportunityFilters,
    touchpointFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    opportunityFieldDefs,
    touchpointFieldDefs,
  ]);
  const relationDisplayFilters = relationFiltersProcessed.displayFilters;
  const relationFilterColumns =
    relationFiltersProcessed.filterColumns as FilterColumn[];
  const handleClearRelationFilter = useCallback(
    (_key: string, clearingInfo?: FilterClearingInfo) => {
      if (
        clearingInfo?.filterGroupType === "opportunity" &&
        clearingInfo.type === "individual" &&
        typeof clearingInfo.index === "number"
      ) {
        const next = {
          ...opportunityFilters,
          filters: opportunityFilters.filters.filter(
            (_f, i) => i !== clearingInfo.index,
          ),
        };
        setOpportunityFilters(next);
      } else if (
        clearingInfo?.type === "consolidated_date_range" &&
        clearingInfo.filterGroupType === "opportunity" &&
        clearingInfo.fieldName
      ) {
        const next = {
          ...opportunityFilters,
          filters: opportunityFilters.filters.filter(
            (f) => "field" in f && f.field !== clearingInfo.fieldName,
          ),
        };
        setOpportunityFilters(next);
      } else if (
        clearingInfo?.filterGroupType === "touchpoint" &&
        clearingInfo.type === "individual" &&
        typeof clearingInfo.index === "number"
      ) {
        const next = {
          ...touchpointFilters,
          filters: touchpointFilters.filters.filter(
            (_f, i) => i !== clearingInfo.index,
          ),
        };
        setTouchpointFilters(next);
      } else if (
        clearingInfo?.type === "consolidated_date_range" &&
        clearingInfo.filterGroupType === "touchpoint" &&
        clearingInfo.fieldName
      ) {
        const next = {
          ...touchpointFilters,
          filters: touchpointFilters.filters.filter(
            (f) => "field" in f && f.field !== clearingInfo.fieldName,
          ),
        };
        setTouchpointFilters(next);
      }
    },
    [
      opportunityFilters,
      touchpointFilters,
      setOpportunityFilters,
      setTouchpointFilters,
    ],
  );
  const {
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    resetToDefaultPinned,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    dndState: { sensors, handleDragEnd },
    getInitializeColumnOrder,
  } = usePersistedColumnState("accounts::list::columns", {
    getDefaultPinnedColumns: () => ["name"],
  });

  // Reference to container element
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // Loading refs for infinite scroll
  const { ref: loadMoreRef, inView } = useInView();

  // Always initialize hook; ignore when stateOverride provided
  const internalCustomFieldSelection = useAccountCustomFieldSelection();
  const fieldIdsParam = stateOverride
    ? stateOverride.columns.customFieldIdsParam
    : internalCustomFieldSelection?.fieldIdsParam || "";

  // Normalize to CustomFieldDefinition[] regardless of override/internal path
  const selectedCustomFieldDefs = useMemo<CustomFieldDefinition[]>(() => {
    if (stateOverride) {
      const cols = stateOverride.columns.selectedCustomColumns || [];
      const defs = cols
        .map((c) => c.customField)
        .filter(Boolean) as CustomFieldDefinition[];
      const map = new Map<string, CustomFieldDefinition>();
      defs.forEach((d) => map.set(d.id, d));
      return Array.from(map.values());
    }
    return internalCustomFieldSelection?.selectedFieldsList || [];
  }, [stateOverride, internalCustomFieldSelection?.selectedFieldsList]);

  // Use account list filters hook
  const internalFilterState = useAccountListFilters();

  // Use stateOverride filters if provided, otherwise use internal state
  const filters =
    stateOverride?.filters.activeFilters || internalFilterState.filters;
  const getDefaultSortSettings = internalFilterState.getDefaultSortSettings;
  const getQueryParams =
    stateOverride?.getQueryParams || internalFilterState.getQueryParams;
  const hasFilters = Object.keys(filters).length > 0;

  // Wrap filter functions to call onFiltersChange when provided
  const updateFilter = useCallback(
    (key: string, value: unknown) => {
      if (stateOverride) {
        stateOverride.filters.updateFilter(key, value);
        // Don't call onFiltersChange when using stateOverride - it manages its own state
      } else {
        internalFilterState.updateFilter(key, value as FilterValue);
        // Only call onFiltersChange for non-override mode
        if (onFiltersChange) {
          const newFilters = { ...filters, [key]: value };
          onFiltersChange(newFilters);
        }
      }
    },
    [stateOverride, internalFilterState, filters, onFiltersChange],
  );

  const clearFilter = useCallback(
    (key: string) => {
      if (stateOverride) {
        stateOverride.filters.clearFilter(key);
        // Don't call onFiltersChange when using stateOverride - it manages its own state
      } else {
        internalFilterState.clearFilter(key);
        // Only call onFiltersChange for non-override mode
        if (onFiltersChange) {
          const newFilters = { ...filters };
          delete newFilters[key];
          onFiltersChange(newFilters);
        }
      }
    },
    [stateOverride, internalFilterState, filters, onFiltersChange],
  );

  const clearAllFilters = useCallback(() => {
    if (stateOverride) {
      stateOverride.filters.clearAllFilters();
      // Don't call onFiltersChange when using stateOverride - it manages its own state
    } else {
      internalFilterState.clearAllFilters();
      // Only call onFiltersChange for non-override mode
      if (onFiltersChange) {
        onFiltersChange({});
      }
    }
  }, [stateOverride, internalFilterState, onFiltersChange]);

  // Fetch external connection links for CRM URLs
  const { data: connections, isLoading: isLoadingConnections } =
    useExternalConnectionLinks();

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings]);

  // Handle clear all filters and reset sort - Updated for sessionStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    resetSortToDefaults();

    // Clear search state from sessionStorage
    clearSearch();
  }, [clearAllFilters, resetSortToDefaults, clearSearch]);

  // When component mounts, set sort from filters (search is now in sessionStorage)
  useEffect(() => {
    // Load sort configuration from sessionStorage
    if (filters.sortColumn) {
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as SortDirection,
      });
    } else {
      // If no sort is set in filters, fall back to defaults for UI consistency
      const defaults = getDefaultSortSettings();
      setSortConfig({
        key: defaults.sortColumn,
        direction: defaults.sortDirection,
      });
    }
  }, [filters.sortColumn, filters.sortDirection, getDefaultSortSettings]);

  // Fetch unified field definitions (standard + custom)
  const { data: fieldDefs } = useFieldDefinitions("accounts");

  // Derive select options for standard enum fields
  const filterOptions = useMemo(() => {
    if (!fieldDefs) return undefined;

    const getEnum = (id: string) =>
      fieldDefs.find((f) => f.id === id && f.has_enum_values)?.enum_values ||
      [];

    return {
      type: getEnum("type"),
      record_type_name: getEnum("record_type_name"),
    } as const;
  }, [fieldDefs]);

  // Define columns, including custom fields if selected
  const columns = useMemo<UnifiedTableColumn<Account>[]>(
    () =>
      getAccountColumns(
        filterOptions,
        selectedCustomFieldDefs,
        connections,
        isLoadingConnections,
      ),
    [filterOptions, selectedCustomFieldDefs, connections, isLoadingConnections],
  );

  // Always initialize hook; ignore when stateOverride provided
  const internalColumnState = useAccountColumnSelection(columns);

  // Use ALL state from override OR internal (never mixed)
  const columnState = stateOverride?.columns ?? internalColumnState;
  const getVisibleColumnDefs = columnState.getVisibleColumnDefs;
  const columnVisibility = columnState.columnVisibility;
  const setColumnVisibility = columnState.setColumnVisibility;
  const newCustomFieldIdsParam = columnState.customFieldIdsParam;
  const selectedCustomColumns = columnState.selectedCustomColumns;
  const resetColumnsToDefault = columnState.resetToDefault;

  // Build column title lookup for cleanup messages
  const columnTitleLookup = useMemo(() => {
    return columns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = col.title;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [columns]);

  // Memoize deduplication of custom fields to avoid recreating on every render
  const uniqueCustomFields = useMemo(() => {
    return [
      ...selectedCustomFieldDefs,
      ...selectedCustomColumns
        .filter((column) => column.customField)
        .map((column) => column.customField as CustomFieldDefinition),
    ].reduce((map, field) => {
      map.set(field.id, field);
      return map;
    }, new Map<string, CustomFieldDefinition>());
  }, [selectedCustomFieldDefs, selectedCustomColumns]);

  // Convert to TanStack columnDefs with resizing enabled
  const columnDefs = useMemo<ColumnDef<Account>[]>(() => {
    // Get column definitions with the deduplicated custom fields
    const allDefs = getAccountColumnDefs(
      filterOptions,
      Array.from(uniqueCustomFields.values()),
      connections,
      isLoadingConnections,
    );

    // Add resizing capability and filter to only show visible columns
    return getVisibleColumnDefs(
      allDefs.map((col) => ({ ...col, enableResizing: true })),
    );
  }, [
    filterOptions,
    uniqueCustomFields,
    getVisibleColumnDefs,
    connections,
    isLoadingConnections,
  ]);

  // Get query parameters for API call with deduplicated field IDs
  const queryParams = useMemo(() => {
    // Efficiently combine and deduplicate both field ID parameters
    const combinedCustomFieldIds = [
      ...(fieldIdsParam ? fieldIdsParam.split(",") : []),
      ...(newCustomFieldIdsParam ? newCustomFieldIdsParam.split(",") : []),
    ]
      .filter(Boolean) // Remove empty strings
      .filter((id, index, array) => array.indexOf(id) === index) // Deduplicate
      .join(",");

    const baseParams: Record<string, unknown> = {
      ...getQueryParams({
        searchTerm: debouncedSearchQuery,
        columns,
        limit: 200,
      }),
      extra_fields: combinedCustomFieldIds,
    };

    // Add relation filters if any exist
    const relationFilters: Record<string, unknown> = {};
    if (hasOpportunityFilters) {
      relationFilters.opportunity_filters = opportunityFilters;
    }
    if (hasTouchpointFilters) {
      relationFilters.touchpoint_filters = touchpointFilters;
    }

    if (Object.keys(relationFilters).length > 0) {
      baseParams.relation_filters = JSON.stringify(relationFilters);
    }

    return baseParams;
  }, [
    getQueryParams,
    debouncedSearchQuery,
    columns,
    fieldIdsParam,
    newCustomFieldIdsParam,
    opportunityFilters,
    hasOpportunityFilters,
    touchpointFilters,
    hasTouchpointFilters,
  ]);

  // Reset ONLY column widths
  const resetColumnWidths = useCallback(() => {
    setColumnSizing({});
  }, [setColumnSizing]);

  // Reset column order to default while preserving pinned columns at the start
  const resetColumnOrder = useCallback(() => {
    setColumnOrder((prev) => {
      const defaultOrder = columnDefs.map((c) => c.id!);
      const pinnedFirst = prev.filter((id) => pinnedColumns.includes(id));
      const unpinnedDefault = defaultOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );
      return [...pinnedFirst, ...unpinnedDefault];
    });
  }, [columnDefs, setColumnOrder, pinnedColumns]);

  // Reset pinned columns - respects saved views if loaded
  const resetPinnedColumns = useCallback(() => {
    if (loadedViewId) {
      // Reset to saved view's configuration
      resetSpecificKeys([
        "accounts::list::columns::pinned",
        "accounts::list::columns::pinned-original-positions",
      ]);
    } else {
      // No saved view - reset to default pinned state
      resetToDefaultPinned();
    }
  }, [loadedViewId, resetSpecificKeys, resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order to defaults
  const resetSortOrder = useCallback(() => {
    const defaults = getDefaultSortSettings();
    // Update UI state
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });

    // Update filters used for API queries
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Comprehensive table reset function (includes filters, sorting, columns, and widths)
  const handleResetTable = useCallback(() => {
    // Clear filters and sorting
    handleClearAllFilters();

    // Reset column visibility to defaults
    resetColumnsToDefault();

    // Reset pinned columns to sync headers/body
    resetPinnedColumns();

    // Reset column order and widths
    resetColumnOrder();
    resetColumnWidths();

    // Reset wrapped columns
    resetWrappedColumns();
  }, [
    handleClearAllFilters,
    resetColumnsToDefault,
    resetColumnOrder,
    resetColumnWidths,
    resetPinnedColumns,
    resetWrappedColumns,
  ]);

  // Use provided data source or fetch accounts with infinite scrolling
  const defaultDataSource = useInfiniteAccounts(
    dataSource ? undefined : (queryParams as any),
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = dataSource || defaultDataSource;

  // Setup for CSV export
  const { exportAccountsAsync } = useAccountsExport();

  // Set up paginated CSV export with custom fields
  const exportAllData = useMemo<() => Promise<Account[]>>(() => {
    if (onExportCSV) {
      return async () => {
        const rows = await onExportCSV();
        return rows as unknown as Account[];
      };
    }
    return () => exportAccountsAsync(queryParams as any);
  }, [onExportCSV, exportAccountsAsync, queryParams]);

  // Filter columns for CSV export based on visibility
  const visibleColumnsForExport = useMemo(() => {
    return columns.filter((col) => {
      const colId = col.key.toString();

      // Check if it's a custom field
      if (col.isCustomField) {
        return selectedCustomColumns.some((c) => c.id === colId);
      }

      // For required columns, always include them (matching table display behavior)
      // This ensures consistency with the table which always shows required columns
      if (col.required === true) {
        return true;
      }

      // For columns with defaultHidden: true, only include if explicitly visible
      if (col.defaultHidden === true) {
        return columnVisibility[colId] === true;
      }

      // For other core columns, check visibility state
      // Include if not explicitly hidden (handles uninitialized state)
      return columnVisibility[colId] !== false;
    });
  }, [columns, columnVisibility, selectedCustomColumns]);

  const { exportData, isExporting, defaultFilename } =
    usePaginatedCSVExport<Account>({
      fetchAllData: exportAllData, // Includes extra_fields when using default exporter
      columns: visibleColumnsForExport,
      filename: `accounts_${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Flatten the pages of data into a single array with memoization
  const accounts = useMemo(() => {
    // Add early return for undefined data to avoid unnecessary processing
    if (!data || !data.pages || data.pages.length === 0) return [];

    // Use slice instead of flatMap if possible for better performance
    if (data.pages.length === 1) return data.pages[0].results;

    return data.pages.flatMap((page) => page.results) || [];
  }, [data]);

  // Total count of accounts
  const totalCount = data?.pages[0]?.total || 0;

  // Check if the metric limit is exceeded
  const isMetricLimitExceeded = totalCount > ACCOUNT_METRIC_LIMIT;

  // Helper function for metric limit tooltip
  const getMetricLimitTooltipText = useCallback(() => {
    return `Apply additional filters to sort on metrics. For performance reasons, sorting on metrics is currently disabled when more than ${ACCOUNT_METRIC_LIMIT} accounts match filters.`;
  }, []);

  // Function to display the sort reset toast notification
  const showSortResetToast = useCallback((columnTitle: string) => {
    const description = `For performance reasons, the sort on ${columnTitle} has been removed. Please filter to fewer than ${ACCOUNT_METRIC_LIMIT} accounts and then try re-applying the sort.`;

    toast.warning("Table sort reset", {
      description,
      duration: 10000,
    });
  }, []);

  // Detect and handle metric sort warning from API
  // This effect responds to external API data, not internal state changes
  useEffect(() => {
    const firstPage = data?.pages[0];
    if (firstPage?.client_warning !== "SORT_ON_METRIC_TOO_MANY_MATCHING") {
      return;
    }

    // Check if we're sorting on a metric column that would trigger the warning
    if (!TOUCHPOINT_METRIC_COLUMNS.has(sortConfig.key)) {
      return;
    }

    // Capture the column name before resetting
    const columnToReset = sortConfig.key;
    const column = columns.find((col) => col.key === columnToReset);

    // Reset to default sort
    setSortConfig({ key: "opportunity_count", direction: "desc" });
    clearFilter("sortColumn");
    clearFilter("sortDirection");

    // Show informative toast
    if (column?.title) {
      showSortResetToast(column.title);
    }
    // Note: We intentionally don't include sortConfig in dependencies
    // to avoid re-triggering when we update it above.
    // The guard (TOUCHPOINT_METRIC_COLUMNS.has) ensures we only reset once.
  }, [data?.pages, clearFilter, columns, showSortResetToast, sortConfig.key]);

  // Column auto-sizing function using utility
  const handleDoubleClickResize = useCallback(
    (_: MouseEvent, columnId: string) => {
      // Skip if loading
      if (isLoading) return;

      requestAnimationFrame(() => {
        try {
          const resizeHandler = createColumnResizeHandler(
            setColumnSizing,
            "accounts-table",
            { sampleSize: 30 }, // Keep the existing sample size optimization
          );

          resizeHandler(_, columnId);
        } catch (e) {
          console.error("Column auto-sizing failed:", e);
          setColumnSizing((prev) => ({ ...prev, [columnId]: 150 }));
        }
      });
    },
    [isLoading, setColumnSizing],
  );

  // Single column resize handler for kebab menu
  const handleSingleColumnResize = useCallback(
    (columnId: string) => {
      if (isLoading) return;
      requestAnimationFrame(() => {
        try {
          const resizeHandler = createSingleColumnResizeHandler(
            setColumnSizing,
            "accounts-table",
            { sampleSize: 30 }, // Keep the existing sample size optimization
          );
          resizeHandler(columnId);
        } catch (e) {
          console.error("Column auto-sizing failed:", e);
          setColumnSizing((prev) => ({ ...prev, [columnId]: 150 }));
        }
      });
    },
    [isLoading, setColumnSizing],
  );

  // Bulk resize handler for autosize all button
  const handleBulkColumnResize = useCallback(
    (columnIds: string[]) => {
      if (isLoading) return;
      requestAnimationFrame(() => {
        try {
          const resizeHandler = createBulkColumnResizeHandler(
            setColumnSizing,
            "accounts-table",
            { sampleSize: 30 }, // Keep the existing sample size optimization
          );
          resizeHandler(columnIds);
        } catch (e) {
          console.error("Bulk column auto-sizing failed:", e);
        }
      });
    },
    [isLoading, setColumnSizing],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      // Clear any filter on this column
      if (filters[columnId] !== undefined) {
        clearFilter(columnId);
      }

      // Reset sort if it was on this column (unless we're loading a saved view)
      if (sortConfig.key === columnId && !isLoadingView) {
        resetSortToDefaults();
        clearFilter("sortColumn");
        clearFilter("sortDirection");

        const columnTitle = columnTitleLookup[columnId];
        if (columnTitle) {
          toast.warning("Table sort reset", {
            description: `Sort on ${columnTitle} has been removed because the column was deselected.`,
            duration: 8000,
          });
        }
      }

      // Update column visibility
      const next: Record<string, boolean> = {
        ...(columnVisibility as Record<string, boolean>),
        [columnId]: false,
      };
      setColumnVisibility(next);
    },
    [
      setColumnVisibility,
      columnVisibility,
      filters,
      clearFilter,
      sortConfig,
      isLoadingView,
      resetSortToDefaults,
      columnTitleLookup,
    ],
  );

  // Initialize TanStack Table with memoized data to prevent unnecessary re-renders
  const table = useReactTable({
    data: accounts, // Use the memoized accounts array
    columns: columnDefs,
    state: {
      columnSizing,
      columnOrder,
      columnVisibility, // Use column visibility from the custom hook
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    defaultColumn: {
      minSize: 80,
      size: 150, // Default starting width
    },
  });

  const rowModel = table.getRowModel();
  const hasWrappedColumns = wrappedColumns.length > 0;
  const rowVirtualizer = useVirtualizer({
    count: rowModel.rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: hasWrappedColumns ? () => 60 : () => 36, // Larger estimate when wrapping
    overscan: 10,
    measureElement: hasWrappedColumns
      ? (el) => el?.getBoundingClientRect().height ?? 60
      : undefined,
  });

  // Initialize column order when columnDefs change
  useEffect(() => {
    const initializeOrder = getInitializeColumnOrder(columnDefs);
    initializeOrder();
  }, [columnDefs, getInitializeColumnOrder]);

  // Load more data when the load more element is in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Search is now managed by useSearchState hook - no need for manual debouncing

  // Handle sort - simplified with better naming
  const handleSort = useCallback(
    (columnKey: string) => {
      // Find column definition
      const column = columns.find((col) => col.key === columnKey);
      if (!column) return;

      // Block metric sorting when limit exceeded
      const isMetricColumn = TOUCHPOINT_METRIC_COLUMNS.has(columnKey);
      if (isMetricColumn && isMetricLimitExceeded) return;

      // Determine new sort direction (toggle or use default)
      const newDirection =
        sortConfig.key === columnKey
          ? sortConfig.direction === "asc"
            ? "desc"
            : "asc"
          : column.defaultSortDirection || "asc";

      // Update local state
      setSortConfig({ key: columnKey, direction: newDirection });

      // Update persistent storage
      updateFilter("sortColumn", columnKey);
      updateFilter("sortDirection", newDirection);
    },
    [columns, sortConfig, updateFilter, isMetricLimitExceeded],
  );

  // Search functions are now provided by useSearchState hook

  // Handle row click to open sidesheet - debounced to prevent multiple clicks
  const handleRowClick = useCallback(
    (accountId: string) => {
      if (customOnRowClick) {
        const account = accounts.find((a) => a.id === accountId);
        if (account) {
          customOnRowClick(account);
        }
      } else {
        // Prevent multiple rapid clicks from causing performance issues
        if (selectedAccount !== accountId) {
          setSelectedAccount(accountId);
          setIsSheetOpen(true);
        }
      }
    },
    [selectedAccount, customOnRowClick, accounts],
  );

  // Render sort icon
  const renderSortIcon = useCallback(
    (column: { key: string; sortable?: boolean }) => {
      if (!column.sortable) return null;

      // Hide sort icon for metric columns when limit exceeded
      const isMetricColumn = TOUCHPOINT_METRIC_COLUMNS.has(
        column.key.toString(),
      );
      if (isMetricColumn && isMetricLimitExceeded) {
        return null;
      }

      return (
        <SortIcon
          isActive={sortConfig.key === column.key}
          direction={
            sortConfig.key === column.key ? sortConfig.direction : null
          }
          showOnHover={false} // Don't show the icon on hover, only when active
        />
      );
    },
    [sortConfig, isMetricLimitExceeded],
  );

  // Render loading state
  const renderLoadingState = () => {
    // Using columnDefs for structure during loading
    const headerGroups = table.getHeaderGroups();

    return (
      <>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  <Skeleton className="h-4 w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {headerGroups[0].headers.map((header) => (
                <TableCell key={`${i}-${header.id}`}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Memoize pinnedColumnPositions calculation at component level to avoid React hooks rules violation
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render table content (headers, body, etc.) without placing DndContext inside thead
  const renderTableContent = () => {
    const rowModel = table.getRowModel();

    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          handleDoubleClickResize={handleDoubleClickResize}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          onRemoveColumn={handleRemoveColumn}
        />
        <TableBody
          className="bg-card relative"
          /* For row virtualization, position relative is needed so absolutely-positioned rows
             are placed correctly inside the tbody without affecting normal flow */
          style={{ height: totalSize }}
        >
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading accounts:{" "}
                {error instanceof Error ? error.message : String(error)}
              </TableCell>
            </TableRow>
          ) : accounts.length === 0 && !isLoading ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                No accounts found.
              </TableCell>
            </TableRow>
          ) : (
            <>
              {virtualRows.map((virtualRow: VirtualItem) => {
                const row = rowModel.rows[virtualRow.index];
                return (
                  <TableRow
                    ref={
                      rowVirtualizer.measureElement as React.Ref<HTMLTableRowElement>
                    }
                    key={row.id}
                    data-index={virtualRow.index}
                    /* Position each virtual row absolutely so it does not reserve layout space
                       and we rely solely on the transform for vertical placement. */
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      display: "flex",
                    }}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleRowClick(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isPinned = pinnedColumns.includes(cell.column.id);
                      const isWrapped = wrappedColumns.includes(cell.column.id);
                      const pinnedLeft = pinnedColumnPositions[cell.column.id];

                      return (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                            ...(isPinned && { left: `${pinnedLeft}px` }),
                          }}
                          className={cn(
                            cell.column.id === sortConfig.key && "bg-muted/40",
                            isPinned && "pinned-column bg-background",
                          )}
                          data-column-id={cell.column.id}
                        >
                          <div
                            className={cn(
                              "cell-content",
                              isWrapped && "wrapped",
                            )}
                            title={String(row.getValue(cell.column.id) || "")}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {/* Load more trigger element */}
              <TableRow ref={loadMoreRef}>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className={cn("h-12", !hasNextPage && "hidden")}
                >
                  {!isFetchingNextPage && hasNextPage ? "Load more" : null}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </>
    );
  };

  return (
    <>
      <Card className="h-full relative flex flex-col">
        {/* Fixed position loading indicator */}
        {isFetchingNextPage && (
          <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-background/90 shadow-md rounded-md px-4 py-2 border">
            <div className="flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="ml-2">Loading more...</span>
            </div>
          </div>
        )}

        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Filter by account name..."
                className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
                onChange={(e) => handleSearchChange(e.target.value)}
                value={searchInputValue}
              />
              {searchInputValue && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
                </Button>
              )}
            </div>
            <InfoTooltip
              trigger={
                <button
                  className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Wildcard search info"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoCircledIcon className="h-3.5 w-3.5" />
                </button>
              }
              side="right"
              sideOffset={5}
            >
              <div className="text-sm space-y-2">
                <p className="font-medium">Supports wildcards</p>
                <ul className="space-y-1">
                  <li>
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">
                      _
                    </code>{" "}
                    matches a single character
                  </li>
                  <li>
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">
                      %
                    </code>{" "}
                    matches zero or more characters
                  </li>
                </ul>
              </div>
            </InfoTooltip>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">
              {isLoading
                ? "Loading..."
                : totalCount > 0
                  ? `${totalCount.toLocaleString()} matching accounts`
                  : "No matching accounts"}
            </div>
            {rightToolbarActions}

            <AccountsColumnSelector
              columnState={
                stateOverride
                  ? {
                      availableColumns: columnState.availableColumns,
                      isColumnSelected: columnState.isColumnSelected,
                      toggleColumn: columnState.toggleColumn,
                      isLoading: columnState.isLoading,
                      error: columnState.error,
                    }
                  : undefined
              }
            />

            {/* Relation Filters Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
              data-testid="relation-filters-button"
            >
              <Filter
                className={`h-4 w-4 ${
                  hasOpportunityFilters || hasTouchpointFilters
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Relation Filters</span>
              {(hasOpportunityFilters || hasTouchpointFilters) && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {(opportunityFilters?.filters?.length || 0) +
                    (touchpointFilters?.filters?.length || 0)}
                </Badge>
              )}
            </Button>

            {/* Autosize All Columns Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const visibleColumnIds = columnOrder.filter(
                      (columnId) =>
                        (columnVisibility as Record<string, boolean>)[
                          columnId
                        ] !== false,
                    );
                    handleBulkColumnResize(visibleColumnIds);
                  }}
                  className="h-8 w-8"
                  disabled={isLoading}
                >
                  <UnfoldHorizontal className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Resize all visible columns to fit their content</p>
              </TooltipContent>
            </Tooltip>

            <ResetTableMenu
              onResetWidths={resetColumnWidths}
              onResetOrder={resetColumnOrder}
              onResetColumns={resetColumnsToDefault}
              onResetTableFilters={handleClearAllFilters}
              onResetPinnedColumns={resetPinnedColumns}
              onResetSort={resetSortOrder}
              onResetWrappedColumns={resetWrappedColumns}
              onResetAll={handleResetTable}
            />

            {/* CSV Export Button */}
            <ExportFilenameDialog
              open={isExportDialogOpen}
              onOpenChange={setIsExportDialogOpen}
              defaultFilename={defaultFilename}
              extension="csv"
              onExport={handleExport}
              disabled={isExporting || accounts.length === 0}
            >
              <CSVExportButton
                onClick={() => setIsExportDialogOpen(true)}
                isExporting={isExporting}
                disabled={isExporting || accounts.length === 0}
                tooltipText="Export accounts data to CSV"
              />
            </ExportFilenameDialog>
          </div>
        </div>

        {/* Active Filters Display – always reserves space (32px) to keep vertical height consistent */}
        <div className="px-4 pb-2 flex items-center justify-between min-h-8 flex-shrink-0">
          {hasFilters ? (
            <ActiveFiltersDisplay
              filters={filters}
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all table filters"
              filterType="table"
            />
          ) : (
            <div></div> /* Empty div to maintain flex layout */
          )}
        </div>
        {/* Relation filters chips */}
        {Object.keys(relationDisplayFilters).length > 0 && (
          <div className="px-4 pb-2 flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={relationDisplayFilters}
              columns={relationFilterColumns}
              clearFilter={handleClearRelationFilter}
              clearAllFilters={clearRelationFilters}
              clearAllText="Clear all relation filters"
              filterType="relation"
            />
          </div>
        )}

        {/* Main Table Container - Add class when resizing */}

        <DndContext
          sensors={sensors}
          collisionDetection={constrainedClosestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToTableBounds]}
        >
          <div
            ref={tableContainerRef}
            className={cn(
              "rounded-md border overflow-y-auto flex-grow",
              table.getState().columnSizingInfo.isResizingColumn &&
                "table-resizing",
            )}
            style={{
              maxWidth: isMobile
                ? "calc(100vw - 2rem)"
                : state === "collapsed"
                  ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                  : "calc(100vw - var(--sidebar-width) - 2rem)",
              minWidth: "640px", // Prevent table from becoming too narrow on small screens
            }}
          >
            <Table
              className="w-full table-fixed"
              style={{ width: table.getTotalSize() }}
              data-table-id="accounts-table"
              data-table-container
            >
              {isLoading ? renderLoadingState() : renderTableContent()}
            </Table>
          </div>
        </DndContext>
      </Card>

      <AccountDetailsSheet
        account={
          selectedAccount
            ? accounts.find((a) => a.id === selectedAccount) || null
            : null
        }
        isOpen={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          // Clear selected account when sheet is closed to allow re-opening same account
          if (!open) {
            setSelectedAccount(null);
          }
        }}
      />

      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="accounts::list"
        entity="account"
      />
    </>
  );
}
